import React, { useEffect, useState, useRef } from 'react';
import { MapPin, Users, Clock, Calendar, FileText, MessageSquare, Tv, HeartHandshake, ChevronDown, CheckCircle, ExternalLink } from 'lucide-react';
import type { ScheduleSession } from '../../../types/schedule.types';
import type { AgendaOption } from '../../../types/agenda.types';
import { generateCalendarUrl, extractSessionDate } from '../../../utils/calendarUtils';
import { getSpeakerTypeLabel } from '../../../../utils/speakerUtils';
import { SpeakerModal } from '../speakers/SpeakerModal';
import { useSpeakers } from '../../hooks/useSpeakers';
import { useEventContext } from '../../contexts/EventContext';
import { useEventDetails } from '../../../hooks/useEventDetails';
import type { Speaker } from '../../types/speaker.types';
interface ExpandedSessionCardProps {
  session: ScheduleSession;
  onClick: () => void;
  onMaterialsClick: () => void;
  agendaOptions?: AgendaOption;
  primaryColor?: string;
  eventDate?: string;
  eventTimeZone?: string;
}
// Helper function to format duration in human-friendly format
const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);

  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h${remainingMinutes}min`;
};

export const ExpandedSessionCard = ({
  session,
  onClick,
  onMaterialsClick,
  agendaOptions,
  primaryColor = '#2563eb',
  eventDate,
  eventTimeZone
}: ExpandedSessionCardProps) => {
  const {
    title,
    description,
    speakers,
    room,
    type,
    image,
    time,
    endTime,
    capacity,
    registeredCount,
    attachments,
    survey,
    isLivestream,
    sponsors,
    durationMinutes
  } = session;
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get event context and fetch speakers data
  const { eventSlug } = useEventContext();
  const { data: speakersData, isLoading: speakersLoading } = useSpeakers(eventSlug);
  const { data: eventDetails } = useEventDetails(eventSlug);
  // Handle materials click with event propagation stopped
  const handleMaterialsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMaterialsClick();
  };

  // Handle speaker click
  const handleSpeakerClick = (e: React.MouseEvent, speakerId: string) => {
    e.stopPropagation();

    // Check if speakers data is still loading
    if (speakersLoading) {
      console.log('Speakers data is still loading...');
      return;
    }

    // Find the full speaker data from the speakers response
    if (speakersData?.data?.speakers) {
      // Convert both IDs to strings for comparison since the API returns strings
      const fullSpeaker = speakersData.data.speakers.find(s => String(s.id) === String(speakerId));
      if (fullSpeaker) {
        setSelectedSpeaker(fullSpeaker);
      } else {
        // If we can't find the speaker in the loaded data, it might be a data mismatch
        console.warn(`Speaker with ID ${speakerId} not found in speakers data`);
        console.warn('Available speaker IDs:', speakersData.data.speakers.map(s => s.id));
      }
    } else {
      // If speakers data isn't loaded yet, we could show a loading state
      console.warn('Speakers data not loaded yet or returned empty');
    }
  };

  // Handle calendar button click
  const handleCalendarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCalendarDropdown(!showCalendarDropdown);
  };
  // Handle specific calendar selection
  const handleAddToCalendar = (e: React.MouseEvent, type: 'google' | 'outlook' | 'apple' | 'outlookcom' | 'office365' | 'yahoo') => {
    e.stopPropagation();
    // IMPORTANT: Prefer eventDate prop (the actual day date) over session's placeholder date
    const actualSessionDate = eventDate || extractSessionDate(session) || new Date().toISOString().split('T')[0];
    const url = generateCalendarUrl(session, type as any, actualSessionDate, eventTimeZone);
    if (type === 'apple') {
      // For Apple Calendar, we need to create a temporary link to download the .ics file
      const link = document.createElement('a');
      link.href = url;
      link.download = `${session.title.replace(/\s+/g, '-')}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // For Google and Outlook, open in a new tab
      window.open(url, '_blank');
    }
    setShowCalendarDropdown(false);
  };
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCalendarDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return <div
      className="w-full border rounded-lg bg-white shadow-sm hover:shadow transition-all cursor-pointer"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="relative flex flex-col md:flex-row">
        {/* Main image - with fixed max height positioned near top */}
        {image && (
          <div className="relative md:absolute md:top-5 md:left-5 md:w-[calc(25%-1.25rem)] h-56 md:h-56 overflow-hidden rounded-lg">
            {/* Blurred background image that fills the container */}
            <div className="absolute inset-0 overflow-hidden">
              <img src={image} alt="" className="w-full h-full object-cover blur-xl scale-110 opacity-15" aria-hidden="true" />
            </div>

            {/* Main image with white background */}
            <div className="relative h-full w-full flex items-center justify-center">
              {/* White background strictly behind the image dimensions */}
              <div className="relative bg-white mx-auto lg:rounded">
                <img
                  src={image}
                  alt={title}
                  className="block object-contain"
                  style={{ maxHeight: '224px', maxWidth: '100%' }}
                />
              </div>
            </div>
          </div>
        )}
        {/* Content */}
        <div className={`flex-1 p-5 ${image ? 'md:ml-[25%] md:min-h-[276px]' : ''}`}>
          <div className="space-y-4">
            {/* Header */}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {type && (  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium capitalize">
                  {type}
                </span>)}
                {isLivestream && <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                    <Tv size={14} />
                    LIVE
                  </span>}
                <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                  <Clock size={14}/>
                  <span>
                    {time} - {endTime}
                  </span>
                  {durationMinutes && durationMinutes > 0 && (agendaOptions?.showSessionDuration !== false) && (
                    <span className="font-medium">
                      ({formatDuration(durationMinutes)})
                    </span>
                  )}
                </span>
                {room && (agendaOptions?.showSessionLocations !== false) && <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                    <MapPin size={14} />
                    <span>{room}</span>
                  </span>}
              </div>
              <h2 className="text-xl font-semibold">{title}</h2>
            </div>
            {/* Description */}
            {description && (agendaOptions?.showSessionDescriptions !== false) && (
              <div
                className="tinymce-content"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
            {/* Speakers */}
            {speakers.length > 0 && (agendaOptions?.showSessionSpeakers !== false) && (
              <div className="space-y-3">
                {(() => {
                  // Group speakers by type locally
                  const speakerGroups = speakers.reduce((acc, speaker) => {
                    const type = speaker.type || 'Speaker';
                    if (!acc[type]) {
                      acc[type] = [];
                    }
                    acc[type].push(speaker);
                    return acc;
                  }, {} as Record<string, typeof speakers>);

                  // Convert to array and sort by first speaker's position in each group
                  const groupedSpeakers = Object.entries(speakerGroups)
                    .map(([type, speakers]) => ({
                      type,
                      speakers,
                      minPosition: Math.min(...speakers.map(s => s.position || 999))
                    }))
                    .sort((a, b) => a.minPosition - b.minPosition);

                  return groupedSpeakers.map((group) => (
                    <div key={group.type} className="space-y-2">
                      <h3 className="text-sm font-medium flex items-center gap-2 text-gray-700">
                        <Users className="w-4 h-4 text-gray-500" />
                        {getSpeakerTypeLabel(group.type)}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {group.speakers.map(speaker => (
                          <div
                            key={speaker.id}
                            className={`flex ${speaker.title ? 'items-start' : 'items-center'} gap-3 p-2 -m-2 rounded-lg transition-colors ${
                              speakersLoading ? 'cursor-wait' : 'cursor-pointer hover:bg-gray-50'
                            }`}
                            onClick={(e) => handleSpeakerClick(e, speaker.id)}
                            title={speakersLoading ? 'Loading speaker details...' : 'Click to view speaker details'}
                          >
                            <div className="relative w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                              {speaker.image ? (
                                <>
                                  {/* Blurred background */}
                                  <div className="absolute inset-0">
                                    <img
                                      src={speaker.image}
                                      alt=""
                                      className="w-full h-full object-cover blur-xl scale-110 opacity-15"
                                      aria-hidden="true"
                                    />
                                  </div>
                                  {/* Main image */}
                                  <div className="relative h-full w-full flex items-center justify-center">
                                    <img
                                      src={speaker.image}
                                      alt={speaker.name}
                                      className="w-full h-full object-contain"
                                      onError={e => {
                                        e.currentTarget.src = 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200&h=200';
                                      }}
                                    />
                                  </div>
                                </>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-600">
                                  <Users size={20} />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900 hover:text-blue-600 transition-colors">{speaker.name}</p>
                              {speaker.title && (
                                <p className="text-xs text-gray-600 truncate">{speaker.title}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
            {/* Session Sponsors */}
            {sponsors && sponsors.length > 0 && <div className="space-y-2">
                <h3 className="text-sm font-medium flex items-center gap-2 text-gray-700">
                  <HeartHandshake className="w-4 h-4 text-gray-500" />
                  <span>Session Sponsors</span>
                </h3>
                <div className="flex flex-wrap gap-2 items-center">
                  {sponsors.map(sponsor => <a key={sponsor.id} href={sponsor.website} target="_blank" rel="noopener noreferrer" className="bg-gray-50 border rounded-lg p-2 hover:bg-gray-100 transition-colors" onClick={e => e.stopPropagation()}>
                      <img src={sponsor.logo} alt={sponsor.name} className="h-6 max-w-[100px] object-contain" />
                    </a>)}
                </div>
              </div>}
            {/* Capacity */}
            {capacity && <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Available Seats</span>
                  <span className="font-medium">
                    {capacity - (registeredCount || 0)} / {capacity}
                  </span>
                </div>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 rounded-full" style={{
                width: `${(registeredCount || 0) / capacity * 100}%`
              }} />
                </div>
              </div>}
            {/* Action Buttons */}
            <div className="flex justify-between items-end gap-3 pt-3">
              {/* Left side: Survey and Materials buttons stacked */}
              <div className="flex flex-col gap-2 items-start">
                {/* Survey Button */}
                {survey && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (survey.url && !survey.isCompleted) {
                        window.open(survey.url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    className={`
                      inline-flex items-center rounded-full font-medium transition-all
                      text-sm px-3 py-1.5 gap-1.5
                      relative overflow-hidden group
                      ${
                        survey.isCompleted
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'
                          : 'text-white hover:shadow-md border'
                      }
                    `}
                    style={{
                      backgroundColor: survey.isCompleted ? undefined : `${primaryColor}20`,
                      color: survey.isCompleted ? undefined : primaryColor,
                      borderColor: survey.isCompleted ? undefined : `${primaryColor}40`
                    }}
                    onMouseEnter={(e) => {
                      if (!survey.isCompleted) {
                        e.currentTarget.style.backgroundColor = `${primaryColor}30`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!survey.isCompleted) {
                        e.currentTarget.style.backgroundColor = `${primaryColor}20`;
                      }
                    }}
                    disabled={survey.isCompleted}
                  >
                    {/* Pulsing background effect when not completed */}
                    {!survey.isCompleted && (
                      <span
                        className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                        style={{ backgroundColor: primaryColor }}
                      ></span>
                    )}

                    {/* Icon */}
                    {survey.isCompleted ? (
                      <CheckCircle size={16} className="flex-shrink-0" />
                    ) : (
                      <MessageSquare size={16} className="flex-shrink-0" />
                    )}

                    {/* Text */}
                    <span>{survey.isCompleted ? 'Survey Completed' : 'Take Survey'}</span>

                    {/* External link icon */}
                    {!survey.isCompleted && survey.url && (
                      <ExternalLink size={12} className="ml-0.5" />
                    )}
                  </button>
                )}

                {/* Materials Button */}
                {attachments && attachments.length > 0 && (agendaOptions?.showSessionAttachments !== false) && (
                  <button
                    onClick={handleMaterialsClick}
                    className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-full border transition-all hover:shadow-md"
                    style={{
                      color: primaryColor,
                      borderColor: `${primaryColor}40`,
                      backgroundColor: `${primaryColor}10`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${primaryColor}20`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${primaryColor}10`;
                    }}
                  >
                    <FileText size={16} />
                    <span>
                      {attachments.length}{' '}
                      {attachments.length === 1 ? 'Material' : 'Materials'}
                    </span>
                  </button>
                )}
              </div>

              {/* Calendar Dropdown on the right */}
              <div className="relative ml-auto" ref={dropdownRef}>
                <button
                  onClick={handleCalendarClick}
                  className="px-4 py-2 text-white rounded-lg flex items-center gap-2 text-sm transition-all"
                  style={{
                    backgroundColor: primaryColor,
                    filter: 'brightness(1)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
                >
                  <Calendar size={16} />
                  <span>Add to Calendar</span>
                  <ChevronDown size={14} />
                </button>
                {showCalendarDropdown && (
                  <div className="absolute right-0 mt-1 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[200px]">
                    <button
                      onClick={e => handleAddToCalendar(e, 'apple')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.apple ? (
                        <img src={eventDetails.calendarIcons.apple} alt="Apple" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">🍎</span>
                      )}
                      Apple
                    </button>
                    <button
                      onClick={e => handleAddToCalendar(e, 'google')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.google ? (
                        <img src={eventDetails.calendarIcons.google} alt="Google" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">📅</span>
                      )}
                      Google <span className="text-gray-400 text-xs">(online)</span>
                    </button>
                    <button
                      onClick={e => handleAddToCalendar(e, 'office365')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.office365 ? (
                        <img src={eventDetails.calendarIcons.office365} alt="Office 365" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">📊</span>
                      )}
                      Office 365 <span className="text-gray-400 text-xs">(online)</span>
                    </button>
                    <button
                      onClick={e => handleAddToCalendar(e, 'outlook')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.outlook ? (
                        <img src={eventDetails.calendarIcons.outlook} alt="Outlook" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">📧</span>
                      )}
                      Outlook
                    </button>
                    <button
                      onClick={e => handleAddToCalendar(e, 'outlookcom')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.outlookcom ? (
                        <img src={eventDetails.calendarIcons.outlookcom} alt="Outlook.com" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">📧</span>
                      )}
                      Outlook.com <span className="text-gray-400 text-xs">(online)</span>
                    </button>
                    <button
                      onClick={e => handleAddToCalendar(e, 'yahoo')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.yahoo ? (
                        <img src={eventDetails.calendarIcons.yahoo} alt="Yahoo" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">📮</span>
                      )}
                      Yahoo <span className="text-gray-400 text-xs">(online)</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Speaker Modal */}
      {selectedSpeaker && (
        <SpeakerModal
          speaker={selectedSpeaker}
          onClose={() => setSelectedSpeaker(null)}
        />
      )}
    </div>;
};